/**
 * GM re-key affordance — decision 14 (approved for M2).
 *
 * A GM who reloads without their `#gm=…` link is re-seated as a player (role
 * is re-derived from credentials every join — INT-09, never inherited). This
 * panel is their way back: an in-room field to re-enter the GM key and
 * restore the GM role, without hunting for the original link.
 *
 * This module owns only the FORM and its inline error. What submitting the
 * key actually does — re-join with the key, or a promote message — is a net/
 * concern the caller wires to `onSubmitGmKey`; the panel stays ignorant of
 * the transport so it can't drift when that mechanism changes.
 *
 * Show it only to a non-GM viewer (the caller decides via room role); a GM
 * already holds the role and needs no prompt.
 */

export interface GmRekeyCallbacks {
  /** The entered key. Wiring re-authenticates with it; on success the viewer
   *  becomes GM and the caller stops rendering this panel. */
  onSubmitGmKey(gmKey: string): void;
}

export interface GmRekeyOptions {
  /** Inline error from a previous attempt (e.g. rejectionMessage("invalid_join")).
   *  Rendered in an assertive live region so a screen reader announces it. */
  error?: string | null;
}

/**
 * Render the re-key form into `root` (idempotent). The key field is a
 * password input so the key is not shoulder-surfable and stays out of any
 * plain-text field history. Empty submissions are ignored (no spurious
 * rejected round-trip).
 */
export function renderGmRekey(
  root: HTMLElement,
  cb: GmRekeyCallbacks,
  options: GmRekeyOptions = {},
): void {
  root.replaceChildren();

  const form = document.createElement("form");
  form.dataset["testid"] = "gm-rekey-form";

  const label = document.createElement("label");
  label.textContent = "Have the GM key? Restore your GM role:";
  const input = document.createElement("input");
  input.name = "gmKey";
  input.type = "password";
  input.autocomplete = "off";
  input.dataset["testid"] = "gm-rekey-input";
  input.setAttribute("aria-label", "GM key");
  label.appendChild(input);

  const button = document.createElement("button");
  button.type = "submit";
  button.dataset["testid"] = "gm-rekey-submit";
  button.textContent = "Restore GM role";

  form.append(label, button);

  // Live region for the inline error; always present so the announcement
  // fires when its text changes rather than when the node is inserted.
  const error = document.createElement("p");
  error.dataset["testid"] = "gm-rekey-error";
  error.setAttribute("role", "alert");
  error.setAttribute("aria-live", "assertive");
  if (options.error != null && options.error !== "") {
    error.textContent = options.error;
  }
  form.appendChild(error);

  form.addEventListener("submit", (event) => {
    event.preventDefault();
    const key = input.value.trim();
    if (key === "") return;
    cb.onSubmitGmKey(key);
  });

  root.appendChild(form);
}
